export type RiskFlagType = 'shell' | 'filesystem' | 'registry' | 'env' | 'http' | 'unknown';
export type RiskScore = 'low' | 'medium' | 'high' | 'unknown';

export interface RiskFlag {
  type: RiskFlagType;
  line: number;
  snippet: string;
}

export interface RiskScanResult {
  score: RiskScore;
  flags: RiskFlag[];
}

const HIGH_RISK_PATTERNS = [
  /CreateObject\s*\(\s*"(WScript\.Shell|Scripting\.FileSystemObject|Shell\.Application)"\s*\)/i,
  /\bShell\s*\(/i,
  /\bKill\s+/i,
  /\bOpen\s+".*"\s+For\s+(Binary|Output|Random)/i,
  /\bWrite\s+#/i,
  /\bMkDir\b|\bRmDir\b|\bFileCopy\b|\bName\b/i,
  /\bReg(Create|Delete|Read|Write)/i,
];

const MEDIUM_RISK_PATTERNS = [
  /\bEnviron\s*\(/i,
  /\bDir\s*\(/i,
  /\bFileLen\b|\bGetAttr\b/i,
  /\bURLDownloadToFileA?\b/i,
  /\bXMLHTTP\b|\bWinHttp\b/i,
];

export function scanVbaCode(code: string): RiskScanResult {
  const lines = code.split(/\r?\n/);
  const flags: RiskFlag[] = [];

  const addFlag = (type: RiskFlagType, lineIndex: number, line: string) => {
    flags.push({
      type,
      line: lineIndex + 1,
      snippet: line.slice(0, 120),
    });
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Check high-risk patterns
    if (HIGH_RISK_PATTERNS.some(pattern => pattern.test(trimmedLine))) {
      if (/WScript\.Shell|Shell\(/.test(trimmedLine)) {
        addFlag('shell', index, line);
      } else if (/File|Dir|Open|Write|Kill|MkDir|RmDir|FileCopy|Name/.test(trimmedLine)) {
        addFlag('filesystem', index, line);
      } else if (/Reg(Create|Delete|Read|Write)/.test(trimmedLine)) {
        addFlag('registry', index, line);
      } else {
        addFlag('unknown', index, line);
      }
    }
    // Check medium-risk patterns
    else if (MEDIUM_RISK_PATTERNS.some(pattern => pattern.test(trimmedLine))) {
      if (/Environ\(/.test(trimmedLine)) {
        addFlag('env', index, line);
      } else if (/XMLHTTP|WinHttp|URLDownloadToFile/.test(trimmedLine)) {
        addFlag('http', index, line);
      } else {
        addFlag('unknown', index, line);
      }
    }
  });

  // Determine overall risk score
  const hasHighRisk = flags.some(f => ['shell', 'filesystem', 'registry'].includes(f.type));
  const score: RiskScore = hasHighRisk ? 'high' : flags.length > 0 ? 'medium' : 'low';

  return { score, flags };
}
